import { ChangeEvent } from 'react';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuthorization } from '@/hooks/use-authorization';
import type { RhythmFields } from './schema';

/**
 * Creates an onChange handler for number inputs that converts empty strings to null
 * and guards against NaN values from parseInt.
 */
const handleNumberChange = (onChange: (value: number | null) => void) => {
  return (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      onChange(null);
      return;
    }
    const parsed = Number.parseInt(e.target.value, 10);
    onChange(Number.isNaN(parsed) ? null : parsed);
  };
};

type RhythmFieldsFormProps<T extends FieldValues & RhythmFields> = {
  form: UseFormReturn<T>;
  /** Override canEditRhythmNotes capability check (optional) */
  canEditRhythmNotes?: boolean;
};

/**
 * Reusable rhythm fields form component for substitute doctors
 * Displays SA (Sans Assistant) and AA (Avec Assistant) sections
 * with on-site (P) and remote (V) slot inputs
 */
export function RhythmFieldsForm<T extends FieldValues & RhythmFields>({
  form,
  canEditRhythmNotes: canEditRhythmNotesProp,
}: RhythmFieldsFormProps<T>) {
  const { t } = useTranslation();
  const { canEditRhythmNotes: canEditRhythmNotesAuth } = useAuthorization();

  // Use prop if provided, otherwise use authorization hook
  const canEditNote = canEditRhythmNotesProp ?? canEditRhythmNotesAuth();

  return (
    <div className="space-y-4">
      <FormLabel>{t('rhythm-structured.title')}</FormLabel>

      {/* Sans Assistant (SA) */}
      <div className="space-y-3 rounded-lg border p-4">
        <span className="font-medium text-muted-foreground text-sm">
          {t('rhythm-structured.without-assistant')}
        </span>
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name={'onSiteSlotsWithoutAssistant' as Path<T>}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs">
                  {t('rhythm-structured.on-site')}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    placeholder=""
                    {...field}
                    value={field.value ?? ''}
                    onChange={handleNumberChange(field.onChange)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'remoteSlotsWithoutAssistant' as Path<T>}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs">
                  {t('rhythm-structured.remote')}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    placeholder=""
                    {...field}
                    value={field.value ?? ''}
                    onChange={handleNumberChange(field.onChange)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* Avec Assistant (AA) */}
      <div className="space-y-3 rounded-lg border p-4">
        <span className="font-medium text-muted-foreground text-sm">
          {t('rhythm-structured.with-assistant')}
        </span>
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name={'onSiteSlotsWithAssistant' as Path<T>}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs">
                  {t('rhythm-structured.on-site')}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    placeholder=""
                    {...field}
                    value={field.value ?? ''}
                    onChange={handleNumberChange(field.onChange)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'remoteSlotsWithAssistant' as Path<T>}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs">
                  {t('rhythm-structured.remote')}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    placeholder=""
                    {...field}
                    value={field.value ?? ''}
                    onChange={handleNumberChange(field.onChange)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* Rhythm Note - visible to everyone, edit restricted by can_edit_rhythm_notes */}
      <FormField
        control={form.control}
        name={'rhythmNote' as Path<T>}
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-xs">
              {t('rhythm-structured.note.label')}
            </FormLabel>
            <FormControl>
              <Textarea
                placeholder={t('rhythm-structured.note.placeholder')}
                {...field}
                value={field.value ?? ''}
                disabled={!canEditNote}
                className="resize-none"
                rows={3}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
